import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { MineState } from './interface';
import { RootState } from 'store/rootReducer';
import { LoadingStatus } from '$store/reels/interface';
import { CoinSquad, MineExploreCard } from '$store/models';
import { estimateEnergy } from '$store/mine/sagas';

const initialState: MineState = {
  loadingStatus: 'loading',
  coins: 0,
  squad: null,
  energy: 0,
  energyDiff: 0,
  energyLimit: 0,
  ts: 0,
  earnPerShake: 1,
  cantEarn: false,

  topLoadingStatus: 'loading',
  topItems: [],
  topTotalCount: 0,

  onboardingPassed: false,

  explore: [],
  exploreLoadingStatus: 'loading',
};

export const { actions, reducer } = createSlice({
  name: 'mine',
  initialState,
  reducers: {
    load(state) {
      state.loadingStatus = 'loading';
    },
    setCoins: (state, action: PayloadAction<number>) => {
      state.coins = action.payload;
    },
    setSquad(state, action: PayloadAction<CoinSquad>) {
      state.squad = action.payload;
    },
    setLoadingStatus(state, action: PayloadAction<LoadingStatus>) {
      state.loadingStatus = action.payload;
    },
    setEnergy: (state, action: PayloadAction<number>) => {
      state.energy = action.payload;
      state.energyDiff = 0;
    },
    setEnergyLimit: (state, action: PayloadAction<number>) => {
      state.energyLimit = action.payload;
    },
    setTS: (state, action: PayloadAction<number>) => {
      state.ts = action.payload;
    },
    addEnergy(state, action: PayloadAction<number>) {
      if (state.energy < state.energyLimit) {
        state.energy += action.payload;
      }
    },
    shake(state) {
      const estimatedEnergy =
        estimateEnergy(state.energy, state.ts, state.energyLimit) - state.energyDiff;
      if (estimatedEnergy >= state.earnPerShake) {
        state.coins += state.earnPerShake;
        state.energyDiff += state.earnPerShake;
        //state.energy = estimatedEnergy - state.earnPerShake;
      }
    },
    setEarnPerShake(state, action: PayloadAction<number>) {
      state.earnPerShake = action.payload;
    },
    loadTop(state) {
      state.loadingStatus = 'loading';
    },
    setTopItems(state, action: PayloadAction<CoinSquad[]>) {
      state.topItems = action.payload;
    },
    setTopTotalCount(state, action: PayloadAction<number>) {
      state.topTotalCount = action.payload;
    },
    setTopLoadingStatus(state, action: PayloadAction<LoadingStatus>) {
      state.topLoadingStatus = action.payload;
    },
    setCantEarn(state, action: PayloadAction<boolean>) {
      state.cantEarn = action.payload;
    },
    setOnboardingPassed(state, action: PayloadAction<boolean>) {
      state.onboardingPassed = action.payload;
    },

    loadExplore(state) {
      state.exploreLoadingStatus = 'loading';
    },
    setExplore(state, action: PayloadAction<MineExploreCard[]>) {
      state.explore = action.payload;
    },
    setExploreLoadingStatus(state, action: PayloadAction<LoadingStatus>) {
      state.exploreLoadingStatus = action.payload;
    },
    updateExploreCard(state, action: PayloadAction<MineExploreCard>) {
      const copy = [...state.explore];
      for (let i = 0; i < copy.length; i++) {
        if (copy[i].id === action.payload.id) {
          copy[i] = { ...action.payload };
          break;
        }
      }

      state.explore = copy;
    },
  },
});

export { reducer as mineReducer, actions as mineActions };

export const mineCoinsSelector = (state: RootState) => state.mine.coins;

export const selectMine = (state: RootState): ReturnType<typeof reducer> => state.mine;
